<script setup lang="ts">
import { publicTicketApi } from '@libero/api-client/public-ticket/api';
import { StorePublicTicketRequest } from '@libero/api-client/public-ticket/types';
import { TicketPriority } from '@libero/api-client/ticket/types';
import { useMapContext } from '@libero/citizen-report/modules/map/hooks/useMap';
import { useTenantStore } from '@libero/hooks/store/useTenantStore';
import { useThemeStore } from '@libero/hooks/store/useThemeStore';
import { useFormContext } from '@libero/hooks/useForm';
import Media from '@libero/organisms/Media/Media.vue';
import { Color } from '@libero/types/Color';
import Alert from '@libero/ui-framework/Alert/Alert.vue';
import Button from '@libero/ui-framework/Button/Button.vue';
import Checkbox from '@libero/ui-framework/Checkbox/Checkbox.vue';
import Cluster from '@libero/ui-framework/Cluster/Cluster.vue';
import FormLayout from '@libero/ui-framework/FormLayout/FormLayout.vue';
import InputGroup from '@libero/ui-framework/InputGroup/InputGroup.vue';
import Label from '@libero/ui-framework/Label/Label.vue';
import Select from '@libero/ui-framework/Select/Select.vue';
import Stack from '@libero/ui-framework/Stack/Stack.vue';
import SubLabel from '@libero/ui-framework/SubLabel/SubLabel.vue';
import Textarea from '@libero/ui-framework/Textarea/Textarea.vue';
import Typography from '@libero/ui-framework/Typography/Typography.vue';
import { getThemeIcon } from '@libero/utilities/theme';
import { useQuery } from '@tanstack/vue-query';
import { lowerFirst } from 'lodash';
import { onMounted } from 'vue';
import { watch } from 'vue';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

interface Props {
  isLoading: boolean;
  onNextStep: () => void;
}

defineProps<Props>();

const { t } = useI18n();

const tenantStore = useTenantStore();
const themeStore = useThemeStore();
const map = useMapContext();
const form = useFormContext<StorePublicTicketRequest>();

const themeId = computed(() => form.values.theme_id);
const ticketSubjectId = computed(() => form.values.ticket_subject_id);

const { data: ticketSubjects } = useQuery({
  queryKey: ['ticket-subjects', themeId],
  queryFn: () => {
    if (!themeId.value) return null;
    return publicTicketApi.subject(themeId.value);
  },
});

const ticketSubject = computed(
  () => ticketSubjects.value?.find((subject) => subject.id === ticketSubjectId.value),
);

const showHighPriorityMessage = computed(
  () =>
    !!form.values.theme_id &&
    ticketSubject.value?.priority === TicketPriority.High &&
    tenantStore.tenant?.show_high_priority_message,
);

const isDisabled = computed(
  () =>
    !form.values.theme_id ||
    !form.values.ticket_subject_id ||
    (showHighPriorityMessage.value && !form.values.should_continue_high_priority),
);

watch(themeId, () => {
  form.values.containers = [];
});

watch(ticketSubject, () => {
  form.values.containers = [];
  map.removeContainer();

  form.values.require_clarification = ticketSubject.value?.require_clarification || false;
});

watch(ticketSubjects, () => {
  form.clearValues(['ticket_subject_id']);
});

onMounted(() => {
  map.setThemeId(undefined);
});
</script>

<template>
  <Typography tag="h1" type="heading" size="xl" bold>
    {{ t('citizen-report.report-title') }}
  </Typography>

  <Stack :gap="2">
    <Typography size="lg">
      {{
        t('citizen-report.report-description', { tenant: lowerFirst(tenantStore.tenant?.title) })
      }}
    </Typography>

    <FormLayout>
      <InputGroup>
        <Label for="theme_id" :value="t('citizen-report.theme')" size="xl" required />

        <Select
          size="lg"
          :options="
            themeStore.themes.map((theme) => ({
              icon: getThemeIcon(theme.key),
              iconColor: Color.primary800,
              label: theme.name,
              value: theme.id,
            }))
          "
          v-bind="form.register('theme_id', true)"
        />
      </InputGroup>

      <InputGroup v-if="form.values.theme_id">
        <Label for="ticket_subject_id" :value="t('citizen-report.subject')" size="xl" required />

        <Select
          size="lg"
          :options="
            ticketSubjects?.map((subject) => ({
              label: subject.external_name || subject.name,
              value: subject.id,
            }))
          "
          v-bind="form.register('ticket_subject_id')"
        />
      </InputGroup>

      <template v-if="showHighPriorityMessage">
        <Alert type="warning" size="lg">
          <strong>
            {{ t('please-note') }}
          </strong>

          {{ tenantStore.tenant?.high_priority_message }}
        </Alert>

        <Checkbox size="lg" v-bind="form.register('should_continue_high_priority')">
          {{ t('report.continue-high-priority') }}
        </Checkbox>
      </template>

      <template
        v-if="
          form.values.theme_id &&
          (!showHighPriorityMessage || form.values.should_continue_high_priority)
        "
      >
        <InputGroup>
          <Label for="media" size="xl" :value="t('citizen-report.media')" />
          <SubLabel :value="t('citizen-report.media-note')" />
          <Media v-bind="form.register('media')" />
        </InputGroup>

        <Alert v-if="form.values.require_clarification" type="warning">
          {{ t('report.require-clarification') }}
        </Alert>

        <InputGroup v-else>
          <Checkbox size="lg" v-bind="form.register('has_explanation')">
            {{ t('citizen-report.has-explanation') }}
          </Checkbox>
        </InputGroup>

        <InputGroup v-if="form.values.require_clarification || form.values.has_explanation">
          <Label for="explanation" :value="t('citizen-report.explanation')" size="xl" />
          <Textarea size="lg" :rows="3" v-bind="form.register('explanation')" />
        </InputGroup>
      </template>
    </FormLayout>

    <Cluster justifyContent="flex-end">
      <Button size="lg" :isDisabled="isDisabled" :isLoading="isLoading" :onClick="onNextStep">
        {{ t('next') }}
      </Button>
    </Cluster>
  </Stack>
</template>
